import * as Slider from '@radix-ui/react-slider';
import cn from 'classnames';
import { FC, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  TOOTH_SLICE_STEP_OPTIONS_MM,
  type ToothSliceOptions,
} from '../../config';
import { resolveSliceOptionTickLabels } from '../../lib';

import styles from './FullHeadMprSliceOptionsControls.module.scss';

type FullHeadMprSliceOptionsControlsProps = {
  options: ToothSliceOptions;
  thicknessOptionsMm: number[];
  className?: string;
  disabled?: boolean;
  showStep?: boolean;
  onChange: (options: ToothSliceOptions) => void;
};

type SliceOptionControlProps = {
  label: string;
  testID: string;
  value: number;
  values: number[];
  className?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
};

const SliceOptionControl: FC<SliceOptionControlProps> = (props) => {
  const { label, testID, value, values, className, disabled, onChange } = props;
  const { formatMessage, formatNumber } = useIntl();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [draggedValues, setDraggedValues] = useState<number[] | null>(null);
  const [isPointerFocus, setIsPointerFocus] = useState(false);
  const pointerDragRef = useRef(false);
  const offeredValues =
    draggedValues ??
    (values.includes(value)
      ? values
      : [...values, value].sort((left, right) => left - right));
  const matchedIndex = offeredValues.indexOf(value);
  const index = draggedIndex ?? matchedIndex;
  const maxIndex = Math.max(1, offeredValues.length - 1);
  const millimetres = formatMessage({ id: 'global.mm', defaultMessage: 'mm' });
  const formattedValue = `${formatNumber(offeredValues[index] ?? value)} ${millimetres}`;
  const labelledMarks = resolveSliceOptionTickLabels({
    selectedIndex: index,
    values: offeredValues,
  });

  const clearDrag = () => {
    pointerDragRef.current = false;
    setDraggedIndex(null);
    setDraggedValues(null);
  };

  return (
    <div className={cn(styles.control, className)}>
      <div className={cn(styles.label, 'p2')}>
        <span>{label}</span>
        <span className={styles.value}>{formattedValue}</span>
      </div>
      <Slider.Root
        className={styles.slider}
        disabled={disabled || offeredValues.length < 2}
        max={maxIndex}
        step={1}
        value={[index]}
        onBlur={() => setIsPointerFocus(false)}
        onKeyDown={() => setIsPointerFocus(false)}
        onPointerCancel={() => {
          clearDrag();
          setIsPointerFocus(false);
        }}
        onPointerDown={() => {
          pointerDragRef.current = true;
          setIsPointerFocus(true);
        }}
        onPointerUp={clearDrag}
        onValueChange={([nextIndex]) => {
          if (pointerDragRef.current) {
            setDraggedValues(offeredValues);
            setDraggedIndex(nextIndex);
          }
        }}
        onValueCommit={([nextIndex]) => {
          clearDrag();
          onChange(offeredValues[nextIndex]);
        }}
      >
        <Slider.Track className={styles.track}>
          <Slider.Range className={styles.range} />
        </Slider.Track>
        <div className={cn(styles.marks, 'p4')} aria-hidden>
          {offeredValues.map((option, optionIndex) => (
            <span
              key={option}
              className={cn(
                styles.mark,
                optionIndex === index && styles.selectedMark,
              )}
              style={{ left: `${(optionIndex / maxIndex) * 100}%` }}
            >
              {labelledMarks[optionIndex] ? formatNumber(option) : null}
            </span>
          ))}
        </div>
        <Slider.Thumb
          className={styles.thumb}
          aria-label={label}
          aria-valuetext={formattedValue}
          data-pointer-focus={isPointerFocus || undefined}
          data-testid={`${testID}-slider`}
        />
      </Slider.Root>
    </div>
  );
};

export const FullHeadMprSliceOptionsControls: FC<
  FullHeadMprSliceOptionsControlsProps
> = (props) => {
  const {
    options,
    thicknessOptionsMm,
    className,
    disabled,
    showStep = true,
    onChange,
  } = props;
  const { formatMessage } = useIntl();

  return (
    <div className={cn(styles.container, className)}>
      {showStep && (
        <SliceOptionControl
          disabled={disabled}
          label={formatMessage({
            id: 'report.slice_step',
            defaultMessage: 'Slice step',
          })}
          testID="full-head-mpr-slice-step"
          value={options.sliceStepMm}
          values={TOOTH_SLICE_STEP_OPTIONS_MM}
          onChange={(sliceStepMm) => onChange({ ...options, sliceStepMm })}
        />
      )}

      <SliceOptionControl
        disabled={disabled}
        label={formatMessage({
          id: 'report.slice_thickness',
          defaultMessage: 'Slice thickness',
        })}
        testID="full-head-mpr-slice-thickness"
        value={options.thicknessMm}
        values={thicknessOptionsMm}
        onChange={(thicknessMm) => onChange({ ...options, thicknessMm })}
      />
    </div>
  );
};
